import { useTranslation } from 'react-i18next'

import { Box, HStack, Text } from '@enechain/ecloud-designsystem'

import { komaEndTime, timeToKomaNumber } from '~/models/komaToTime'

/**
 * グラフの共通 tooltip。
 *
 * design の `design/js/charts.js` にある `darkTooltipContent` の移植。 8080 では
 * **20 画面が同じ helper を共有**しており、 同ファイルにも「数値をどちらか片方だけ
 * 直すと画面によって別の tooltip が出るので、 必ずこの TIP を直すこと」 と
 * 単一ソースであることが明記されている
 * (2026-08-28 デザイナー指摘「グラフの tooltip の UI は 8080 準拠」)。
 *
 * 当初は消費者が 48点データ 1 画面だけだったため page 配下に置いていたが、
 * 2026-08-31 にスポット価格のグラフが 2 つ目の消費者になったので
 * `frontend/apps/ebalance/CLAUDE.md`「複数 page で実際に使われてから共有
 * directory へ昇格する」 に従ってここへ昇格した。 8080 側も 20 画面が 1 つの
 * helper を共有しており、 共有が本来の形。
 *
 * design の実値 (`charts.js` の `TIP`):
 *
 * | | 値 |
 * |---|---|
 * | 器 | `--surface-primary` / 1px `--border-low` / radius 8 / padding 8px 10px |
 * | | min-width 120 / line-height 1.5 / `--shadow-lg` / pointer-events none |
 * | 見出し | 700 / 14px / 下線 1px `--border-low` / margin-bottom 4 / padding-bottom 3 |
 * | 系列行 | gap 6 / margin-top 2 |
 * | 色チップ | 8x8 / radius 2 / 系列色 |
 * | 系列名 | 13px / `--text-high-emphasis` (「名前:」 とコロンを付ける) |
 * | 値 | 700 / 16px / 右寄せ / `Math.round().toLocaleString()` |
 *
 * 見出しは design の `_tipKomaParts` と同じで、 `HH:MM` 形式なら
 * 「コマ N」 (左) + 「HH:MM – HH:MM」 (右) に整形する。 48点データの X 軸ラベルは
 * `komaIndexToTime()` が作る `HH:MM` なので、 この分岐に入るのが 8080 の挙動
 * (2026-08-28 レビュー指摘。 当初 X 軸をコマ番号と読み違えて素の見出しにしていた)。
 *
 * design は加えて weather-strip のある画面で天気アイコンを見出しに添えるが、
 * それは DOM を走査して拾う実装で、 48点データに weather-strip は無い。 移植しない。
 */
/**
 * recharts が `content` 要素へ cloneElement で注入する props のうち、 本 component が
 * 読むものだけを optional で受ける。
 *
 * recharts 側の `TooltipContentProps` をそのまま使うと、 `content={<ChartTooltip />}`
 * の JSX に全 props が必須になり書けない (注入は実行時なので静的には空)。 同じ理由で
 * `PositionChart` も局所の `TooltipRenderProps` を定義しており、 その慣行に合わせる。
 */
type ChartTooltipProps = {
  active?: boolean
  label?: string | number
  payload?: ReadonlyArray<{
    name?: string | number
    value?: number | string
    color?: string
    dataKey?: string | number
  }>
  /**
   * 数値の表示書式。 省略時は整数へ丸めて桁区切り (design `js/charts.js` の
   * `TIP` 既定。 MW 系チャート向け)。
   *
   * design 側は共通 tooltip を書き換えず、 **呼び出し側が payload の値を文字列に
   * してから渡す**ことで丸めを回避している (spot-price.html の `_smTooltipContent`
   * が `toFixed(2)` するのがそれ)。 React では payload を差し替える wrapper を
   * 挟むより、 書式そのものを渡す方が素直なのでこの prop にした。
   * 単位まで含めたい画面はここで付ける。
   *
   * 第 2 引数の `dataKey` は、 **1 つのチャートに単位の違う系列が混在する画面**の
   * ため (需要想定は左軸 kWh と右軸 ℃ を同時に描き、 丸めるのは kWh 側だけ)。
   * 引数を 1 つしか取らない既存の呼び出しはそのまま通る。
   */
  formatValue?: (value: number, dataKey?: string) => string
}

/**
 * 見出しラベルが `HH:MM` なら「コマ N」 と「HH:MM – HH:MM」 に分解する
 * (design `js/charts.js` の `_tipKomaParts` と同じ規則)。 `HH:MM` でなければ null。
 *
 * コマ番号は design と同じ **1 origin** (`00:00` → コマ 1)。
 */
function resolveKomaParts(
  label: string | number | undefined,
  t: (key: string, options: { koma: number }) => string,
): { komaLabel: string; range: string } | null {
  if (typeof label !== 'string' || !/^\d{1,2}:\d{2}$/.test(label)) return null
  return {
    komaLabel: t('common:chartTooltip.koma', {
      koma: timeToKomaNumber(label),
    }),
    // design は en dash (–) で繋ぐ。
    range: `${label} – ${komaEndTime(label)}`,
  }
}

/* eslint-disable ebalance/no-adhoc-typography -- chart tooltip の typography は
   design `js/charts.js` の `TIP` が SoT (labelFs 14 / nameFs 13 / valueFs 16)。
   éclair スケール (10/12/14/16/20/24) へ写すと 見出し = md(14) / 値 = lg(16) は
   同値、 系列名だけ design の 13 に対し sm(12) で 1px 小さくなる。 design 自身が
   同じ箇所のコメントで「name = sm」 と DS 準拠を宣言しているのでスケールに乗せる。
   `PositionChart` の tooltip も同じ判断で token を使っている。 */
export const ChartTooltip = ({
  active,
  payload,
  label,
  formatValue = (v) => Math.round(v).toLocaleString(),
}: ChartTooltipProps): React.ReactElement | null => {
  const { t } = useTranslation()

  if (active !== true || payload === undefined) return null

  // design: `payload.filter(p => p.value != null)`。 値の無い系列は行ごと出さない。
  const items = payload.filter((p) => p.value !== undefined && p.value !== null)
  if (items.length === 0) return null

  const komaParts = resolveKomaParts(label, t)

  return (
    <Box
      minWidth="120px"
      padding="8px 10px"
      color="Text.highEmphasis"
      lineHeight={1.5}
      background="Surface.primary"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      // 影は DS の `shadLg` (UI 辞書 dictionary/tokens.md の `--shadow-*` = DS shadows。
      // Chakra 既定の `lg` は DS の影ではない)。 同時同量のチャート tooltip (DemandTooltip) と同じ (#7064)
      boxShadow="shadLg"
      pointerEvents="none"
    >
      {label !== undefined && label !== '' && (
        <Box
          marginBottom="4px"
          paddingBottom="3px"
          fontSize="md"
          fontWeight="bold"
          borderBottomWidth="1px"
          borderBottomStyle="solid"
          borderBottomColor="Border.lowEmphasis"
        >
          {komaParts === null ? (
            label
          ) : (
            // design: 左に「コマ N」、 右に開始–終了レンジ (weight 400 / 1 段小さく)。
            <HStack
              alignItems="baseline"
              justifyContent="space-between"
              gap={3}
            >
              <Text as="span">{komaParts.komaLabel}</Text>
              <Text as="span" fontSize="sm" fontWeight="normal">
                {komaParts.range}
              </Text>
            </HStack>
          )}
        </Box>
      )}
      {items.map((p, i) => (
        <HStack
          // 系列名は重複しうる (同名の系列を 2 本描く画面がある) ので index を併用する。
          key={`${String(p.name ?? '')}-${String(i)}`}
          alignItems="center"
          gap="6px"
          marginTop="2px"
        >
          <Box
            flexShrink={0}
            width="8px"
            height="8px"
            // 系列色は Recharts が payload に載せる実値 (DS token では解決できない)。
            background={p.color ?? undefined}
            borderRadius="sm"
          />
          <Text as="span" color="Text.highEmphasis" fontSize="sm">
            {p.name}:
          </Text>
          <Text as="span" marginLeft="auto" fontSize="lg" fontWeight="bold">
            {typeof p.value === 'number'
              ? formatValue(
                  p.value,
                  typeof p.dataKey === 'string' ? p.dataKey : undefined,
                )
              : p.value}
          </Text>
        </HStack>
      ))}
    </Box>
  )
}
